/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { isArray } from '@ember/array';
import { action, computed } from '@ember/object';
import { setDiff, alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Options from '@linc-technologies/linced-common/enums/options';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import { byKeys } from '@linc-technologies/linced-common/utils/arrays';
import {
  getCurrentMilestone,
  generateExpectationData,
  generateProgressData,
} from '@linc-technologies/linced-common/utils/progressions';
import {
  loadSavedPreferences,
  mapGradesByLearner,
  remapSummariesByLearner,
} from 'linced-app/utils/data-cards';

export default class SubjectController extends Controller {
  @service
  colours;

  @service
  dates;

  @service
  options;

  @service
  personOptions;

  @service
  router;

  @service
  session;

  queryParams = ['topic'];
  topic = null;
  topics = [];

  // personCardOpen determines whether the person nav card is open.
  @tracked
  personCardOpen = false;

  // selectedPerson is the person whose card is being displayed, if applicable.
  @tracked
  selectedPerson = null;

  @computed('session.activeSchool')
  get progressChartColors() {
    return this.colours.charts(this.session.school);
  }

  @optionAlias('hero:lock:milestones')
  milestonesLocked;
  @optionAlias(Options.CHARTS_SHOW_ALL_COLOURS)
  showAllColours;

  @computed('model.levels.@each.sequence')
  get levels() {
    return this.model.levels
      .slice()
      .sort(
        (a, b) =>
          a.sequence - b.sequence ||
          (a.label || '').localeCompare(b.label || ''),
      );
  }

  @alias('model.subject.milestones')
  milestones;

  @computed('model.{milestones.[],group.learners.[]}')
  get learnerMilestones() {
    let members = this.get('model.group.sortedCurrentLearners');
    if (!isArray(members)) {
      return {};
    }
    return members.reduce((map, member) => {
      let person = member.get('person');
      map[person.id] = getCurrentMilestone(
        this.milestones,
        person,
        this.dates.rolloverMonthDate,
      );
      return map;
    }, {});
  }

  @computed('model.subjectSummaries.@each.{personId,subjectId}')
  get learnerSummaries() {
    return remapSummariesByLearner(this.model.subjectSummaries);
  }

  @computed('model.{milestones.[],levels.[],expectations.[]}')
  get expectations() {
    return generateExpectationData(
      this.model.expectations,
      this.milestones,
      this.levels,
      this.showAllColours,
    );
  }

  @computed('model.{progressions.[],group.learners.[]}')
  get learnerProgressions() {
    let members = this.get('model.group.sortedCurrentLearners');
    if (!isArray(members)) {
      return {};
    }

    // Build initial empty map of personId -> array.
    let map = members.reduce((map, member) => {
      let person = member.get('person');
      map[person.id] = [];
      return map;
    }, {});

    // Populate with records.
    let progressions = this.get('model.progressions');
    if (!isArray(progressions)) {
      return map;
    }
    return progressions.reduce((map, progression) => {
      let personId = progression.person_id;
      // Ignore people who aren't current group members, since we won't show them anyway.
      if (isArray(map[personId])) {
        map[personId].push(progression);
      }
      return map;
    }, map);
  }

  @computed('model.grades.[]')
  get learnerGrades() {
    return mapGradesByLearner(this.model.grades);
  }

  @tracked
  selectedAssessments = [];
  @tracked
  selectedModules = [];
  @tracked
  selectedProgressSubjects = [];
  @tracked
  selectedGoalSubjects = [];

  @setDiff('model.assessments', 'selectedAssessments')
  remainingAssessments;

  @tracked
  editDialog;

  @tracked
  optionsSaveInProgress;

  @tracked
  orientation;

  @computed
  get userOptions() {
    return this.personOptions.getAllDataCardOptions(this.session.person, [
      'assessmentIds',
      'assessmentModuleIds',
      'subjectIds',
      'progressIds',
    ]);
  }

  @computed('goalSubjects.@each.id', 'userOptions.[]')
  get isSelectedByDefault() {
    let userOptions = this.personOptions.getAllDataCardOptions(
      this.session.person,
      ['assessmentIds', 'assessmentModuleIds', 'subjectIds', 'progressIds'],
    );
    const userSubjectIds = userOptions.subjectIds || [];
    return this.goalSubjects.reduce((acc, goalSubject) => {
      acc[goalSubject.id] = userSubjectIds.includes(goalSubject.id);
      return acc;
    }, {});
  }

  // Initialize component when model loads
  init() {
    super.init(...arguments);
    this.selectedAssessments = [];
    this.selectedModules = [];
    this.selectedProgressSubjects = [];
    this.selectedGoalSubjects = [];
  }

  // Load all saved options from personOptions service

  @computed('model.allSubjects.@each.trackable')
  get progressSubjects() {
    const allSubjects = this.get('model.allSubjects');
    if (!allSubjects) return [];

    return allSubjects
      .filter(
        (subject) =>
          subject.trackable &&
          subject.visibleToTeachers &&
          !isEmpty(subject.id),
      )
      .sort(byKeys('sequence', 'label'));
  }

  @computed('model.allSubjects.@each.{isTopic,visibleInMenu}')
  get goalSubjects() {
    const allSubjects = this.get('model.allSubjects');
    if (!allSubjects) return [];
    return allSubjects
      .filter(
        (subject) =>
          !subject.isTopic && subject.visibleToTeachers && !isEmpty(subject.id),
      )
      .sort(byKeys('sequence', 'label'));
  }

  @computed('model.allSubjects.@each.{isTopic,visibleInMenu,subjectTypeName}')
  get groupedGoalSubjects() {
    const allSubjects = this.get('model.allSubjects');
    if (!allSubjects) return [];

    const filteredSubjects = allSubjects
      .filter(
        (subject) =>
          !subject.isTopic &&
          subject.visibleToTeachers &&
          !isEmpty(subject.id) &&
          subject.get('subjectTypeName') !== 'Progress', // Exclude 'Progress' type
      )
      .sort(byKeys('sequence', 'label'));

    // Group subjects by subjectTypeName
    const groupedSubjects = [];
    const subjectTypeMap = {};

    filteredSubjects.forEach((subject) => {
      const typeName = subject.get('subjectTypeName') || 'Other';

      if (!subjectTypeMap[typeName]) {
        const group = {
          typeName: typeName,
          subjects: [],
        };
        groupedSubjects.push(group);
        subjectTypeMap[typeName] = group;
      }

      subjectTypeMap[typeName].subjects.push(subject);
    });

    return groupedSubjects;
  }

  @action
  currentMilestone(person) {
    return this.get(`learnerMilestones.${person.id}`);
  }

  @action
  previousMilestone(currentMilestone) {
    let currentIndex = this.milestones.indexOf(currentMilestone);
    return this.milestones[currentIndex - 1];
  }

  @action
  chartData(records) {
    return generateProgressData(this.milestones, this.levels, records);
  }

  @action
  updateRecords() {
    this.model.progressions.update();
  }

  @action
  openDialog() {
    // Reset/reload user preferences.
    loadSavedPreferences(this, this.model, [
      'assessmentIds',
      'assessmentModuleIds',
      'subjectIds',
      'progressIds',
    ]);
    this.editDialog = true;
  }

  @action
  closeDialog() {
    this.editDialog = false;
  }

  @action
  setOrientation(orientation) {
    this.orientation = orientation;
  }

  @action
  toggleSelectedModule(module) {
    if (this.selectedModules.includes(module)) {
      this.selectedModules = this.selectedModules.filter((m) => m !== module);
    } else {
      this.selectedModules = [...this.selectedModules, module];
    }
  }

  @action
  toggleSelectedProgressSubjects(subject) {
    if (this.selectedProgressSubjects.includes(subject)) {
      this.selectedProgressSubjects = this.selectedProgressSubjects.filter(
        (s) => s !== subject,
      );
    } else {
      this.selectedProgressSubjects = [
        ...this.selectedProgressSubjects,
        subject,
      ];
    }
  }

  @action
  toggleSelectedGoalSubjects(subject) {
    if (this.selectedGoalSubjects.includes(subject)) {
      this.selectedGoalSubjects = this.selectedGoalSubjects.filter(
        (s) => s !== subject,
      );
    } else {
      this.selectedGoalSubjects = [...this.selectedGoalSubjects, subject];
    }
  }

  @action
  saveSelections() {
    this.optionsSaveInProgress = true;
    let assessmentIds = this.selectedAssessments.map(
      (assessment) => assessment.id,
    );
    let moduleIds = this.selectedModules.map((module) => module.id);
    let progressSubjectIds = this.selectedProgressSubjects.map(
      (subject) => subject.id,
    );
    let goalSubjectIds = this.selectedGoalSubjects.map((subject) => subject.id);

    this.personOptions
      .updateAllDataCardOptions(this.session.person, {
        assessmentIds: assessmentIds,
        assessmentModuleIds: moduleIds,
        subjectIds: goalSubjectIds,
        progressIds: progressSubjectIds,
      })
      .then(() => {
        this.editDialog = false;
        this.router.refresh('group.triangulation.subject');
      })
      .catch(() => {
        this.toaster.error('Error saving selections. Please try again.');
      })
      .finally(() => {
        this.optionsSaveInProgress = false;
      });
  }

  @action
  addAssessment(assessment) {
    this.selectedAssessments = [...this.selectedAssessments, assessment];
  }

  @action
  removeAssessment(assessment) {
    this.selectedAssessments = this.selectedAssessments.filter(
      (a) => a !== assessment,
    );
  }

  @action
  openPersonCard(person) {
    this.personCardOpen = true;
    this.selectedPerson = person;
  }
}
